import React, { useState, useEffect } from 'react';
import { AppData } from '../types';
import { AppIcon } from '../components/AppIcon';
import { StoreBadge } from '../components/StoreBadge';
import { appCatalog, CategoryResponse } from '../services/appCatalogService';
import { Layers, Star, ArrowRight, Loader2, Search, TrendingUp, Sparkles, Filter, Database, AlertCircle } from 'lucide-react';

interface CategoryPageViewProps {
  categorySlug?: string | null;
  onSelectApp: (appId: string) => void;
  onNavigate: (route: string) => void;
}

export const ALL_CATEGORIES = [
  { slug: 'travel', name: 'Travel', description: 'Navigation, ride hailing, flight trackers, vacation rentals, and itinerary planners.' },
  { slug: 'productivity', name: 'Productivity', description: 'Task managers, calendars, document editors, email clients, and workflow tools.' },
  { slug: 'education', name: 'Education', description: 'Language learning, coding tutors, reference libraries, and academic study apps.' },
  { slug: 'photo-video', name: 'Photo & Video', description: 'Camera filters, video editors, visual design tools, and creator suites.' },
  { slug: 'finance', name: 'Finance', description: 'Personal budgeting, banking, crypto tracking, and investment analytics.' },
  { slug: 'health-fitness', name: 'Health & Fitness', description: 'Workout trackers, running telemetry, meditation, and nutrition logs.' },
  { slug: 'utilities', name: 'Utilities', description: 'System tools, calculators, file managers, widgets, and network helpers.' },
  { slug: 'social-networking', name: 'Social Networking', description: 'Messaging, community feeds, microblogging, and real-time voice.' },
  { slug: 'business', name: 'Business', description: 'Video conferencing, team collaboration, document signing, and CRM apps.' },
  { slug: 'games', name: 'Games', description: 'Action, puzzle, strategy, casual, role-playing, and multiplayer games.' },
  { slug: 'entertainment', name: 'Entertainment', description: 'Movie streaming, television networks, comic readers, and fandom apps.' },
  { slug: 'music', name: 'Music', description: 'Streaming audio, synthesizers, tuner utilities, and music discovery.' },
  { slug: 'sports', name: 'Sports', description: 'Live score trackers, league standings, sports streaming, and team news.' },
  { slug: 'lifestyle', name: 'Lifestyle', description: 'Dating, smart home control, fashion, horoscopes, and community lifestyles.' },
  { slug: 'news', name: 'News', description: 'Daily newspapers, breaking bulletins, RSS readers, and editorial journals.' },
  { slug: 'shopping', name: 'Shopping', description: 'Retail storefronts, marketplace auctions, coupon finders, and fashion stores.' },
  { slug: 'food-drink', name: 'Food & Drink', description: 'Food delivery, restaurant reservations, recipe books, and coffee ordering.' },
  { slug: 'weather', name: 'Weather', description: 'Live Doppler radar, severe weather alerts, hourly forecasts, and air quality.' },
  { slug: 'navigation', name: 'Navigation', description: 'Turn-by-turn GPS navigation, transit maps, and traffic telemetry.' },
  { slug: 'medical', name: 'Medical', description: 'Telehealth consultations, symptom checkers, and clinical references.' },
  { slug: 'graphics-design', name: 'Graphics & Design', description: 'Vector illustration, graphic design suites, and 3D modeling.' },
  { slug: 'reference', name: 'Reference', description: 'Dictionaries, translation lexicons, and knowledge encyclopedias.' }
];

export const CategoryPageView: React.FC<CategoryPageViewProps> = ({
  categorySlug,
  onSelectApp,
  onNavigate
}) => {
  const [apps, setApps] = useState<AppData[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('trending');
  const [storeFilter, setStoreFilter] = useState<'all' | 'apple' | 'google'>('all');
  const [isLiveRefreshUnavailable, setIsLiveRefreshUnavailable] = useState<boolean>(false);

  const matchedCategory = ALL_CATEGORIES.find((c) => c.slug === categorySlug) || (categorySlug ? {
    slug: categorySlug,
    name: categorySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    description: `Mobile applications indexed in the ${categorySlug} category.`
  } : null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    if (categorySlug) {
      appCatalog
        .getCategory(categorySlug, {
          q: searchQuery,
          sort: sortBy as any,
          store: storeFilter,
          limit: 100
        })
        .then((res) => {
          if (isMounted) {
            setCategoryData(res);
            setApps(res.apps || []);
            setIsLiveRefreshUnavailable(res.liveRefreshAvailable === false);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.warn('[CategoryPageView] Error fetching category, relying on database fallback:', err);
          if (isMounted) {
            setIsLiveRefreshUnavailable(true);
            setIsLoading(false);
          }
        });
    } else {
      // Categories Index Hub
      appCatalog
        .getCategoryCounts()
        .then((counts) => {
          if (isMounted) {
            setCategoryCounts(counts || {});
            setIsLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) setIsLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [categorySlug, searchQuery, sortBy, storeFilter]);

  const activeCategory = categoryData ? {
    slug: categoryData.slug,
    name: categoryData.category,
    description: categoryData.description
  } : matchedCategory;

  const topGrowthApps = apps.slice(0, 3).filter(a => a.growthScore >= 80);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Layers className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>{categorySlug ? 'Category Intelligence' : 'Public Categories Index'}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight flex items-center gap-3">
            <span>{activeCategory ? `${activeCategory.name} Apps` : 'App Store Categories'}</span>
            {activeCategory && apps.length > 0 && (
              <span className="text-sm font-bold px-3 py-1 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 font-mono">
                {categoryData?.total || apps.length} Indexed
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {activeCategory ? activeCategory.description : 'Explore indexed applications, ranking velocity, and publisher portfolios grouped by official App Store genres.'}
          </p>
        </div>

        {activeCategory && (
          <button
            onClick={() => onNavigate('/categories')}
            className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer self-start md:self-auto"
          >
            ← View all categories
          </button>
        )}
      </div>

      {/* Non-blocking Notice when live provider refresh is unavailable */}
      {isLiveRefreshUnavailable && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 text-amber-900 text-xs shadow-xs animate-fadeIn">
          <Database className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="leading-relaxed">
            Live metadata refresh temporarily unavailable. Showing the latest indexed AppStatsView data.
          </p>
        </div>
      )}

      {/* Single Category View */}
      {activeCategory ? (
        <div className="space-y-6">
          {/* Filter & Search Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search in ${activeCategory.name}...`}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="trending">Sort: Highest Growth Score</option>
                <option value="rank">Sort: Search Position (Top #)</option>
                <option value="rating">Sort: Highest Rating</option>
                <option value="reviews">Sort: Most Reviewed</option>
                <option value="updated">Sort: Recently Updated</option>
              </select>

              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value as any)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Stores</option>
                <option value="apple">Apple App Store</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="py-24 flex flex-col items-center justify-center space-y-3">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-xs text-slate-500 font-medium">Loading {activeCategory.name} applications...</p>
            </div>
          ) : apps.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 space-y-3">
              <h3 className="text-base font-bold text-slate-900">No applications matched your filter</h3>
              <p className="text-xs text-slate-500">Try clearing the search box or exploring all categories.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSortBy('trending');
                  setStoreFilter('all');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 cursor-pointer"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apps.map((app) => (
                <div
                  key={app.id}
                  onClick={() => onSelectApp(app.appStoreId ? String(app.appStoreId) : app.id)}
                  className="bg-white rounded-3xl p-5 border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between group space-y-4"
                >
                  <div className="flex items-start gap-3.5">
                    <AppIcon app={app} size="lg" className="w-14 h-14 rounded-2xl flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                          {app.name}
                        </h4>
                        <StoreBadge store={app.primaryStore} size="xs" />
                      </div>
                      <p className="text-xs text-slate-500 truncate">{app.developer}</p>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                        <span className="flex items-center gap-1 font-bold text-amber-500">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          <span>{app.rating.toFixed(1)}</span>
                        </span>
                        <span>•</span>
                        <span className="text-[11px] text-slate-400">
                          {app.totalReviews > 1000 ? `${(app.totalReviews / 1000).toFixed(0)}k reviews` : `${app.totalReviews} reviews`}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-slate-400 text-[11px] px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200/60">
                        Pos #{app.searchPosition || app.currentRank}
                      </span>
                      <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                        Score {app.growthScore}
                      </span>
                    </div>
                    <span className="font-bold text-blue-600 group-hover:underline flex items-center gap-1">
                      <span>Inspect</span>
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Categories Hub Grid with Real DB Counts */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ALL_CATEGORIES.map((cat) => {
            const count = categoryCounts[cat.name] || 0;
            return (
              <div
                key={cat.slug}
                onClick={() => onNavigate(`/category/${cat.slug}`)}
                className="bg-white rounded-3xl p-6 border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer space-y-3 group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                      {cat.name}
                    </h3>
                    {count > 0 && (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        {count} apps
                      </span>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {cat.description}
                </p>
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-400">
                  <span>Database Indexed</span>
                  <span className="text-blue-600 font-semibold">Explore category →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
